import type { ReactNode, CSSProperties, MouseEventHandler } from 'react'
import { cn } from '../utils/cn'

export const CARD_SIZES = {
  sm: { width: 120, height: 160 },
  md: { width: 160, height: 240 },
} as const

export type CardSize = keyof typeof CARD_SIZES

type CardShellProps = {
  size?: CardSize
  borderColor?: string
  background?: string
  className?: string
  style?: CSSProperties
  onClick?: () => void
  onMouseEnter?: MouseEventHandler<HTMLDivElement>
  onMouseLeave?: MouseEventHandler<HTMLDivElement>
  onMouseMove?: MouseEventHandler<HTMLDivElement>
  onKeyDown?: (e: React.KeyboardEvent<HTMLDivElement>) => void
  disabled?: boolean
  title?: string
  role?: string
  tabIndex?: number
  'aria-disabled'?: boolean
  'aria-label'?: string
  children: ReactNode
}

const BORDER_RADIUS: Record<CardSize, number> = {
  sm: 10,
  md: 12,
}

const DEFAULT_BORDER_COLOR = 'rgba(160,120,200,0.3)'
const DEFAULT_BG = 'linear-gradient(160deg, #2A2040, #1E1830)'

export function CardShell({
  size = 'sm',
  borderColor = DEFAULT_BORDER_COLOR,
  background = DEFAULT_BG,
  className,
  style,
  onClick,
  onMouseEnter,
  onMouseLeave,
  onMouseMove,
  onKeyDown,
  disabled = false,
  title,
  role,
  tabIndex,
  'aria-disabled': ariaDisabled,
  'aria-label': ariaLabel,
  children,
}: CardShellProps) {
  const { width, height } = CARD_SIZES[size]
  const borderRadius = BORDER_RADIUS[size]

  return (
    <div
      className={cn(
        'relative flex flex-col overflow-hidden',
        disabled && 'opacity-40 cursor-not-allowed',
        className,
      )}
      style={{
        width,
        height,
        background,
        border: `1.5px solid ${borderColor}`,
        borderRadius,
        ...style,
      }}
      onClick={!disabled ? onClick : undefined}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      onMouseMove={onMouseMove}
      onKeyDown={onKeyDown}
      title={title}
      role={role}
      tabIndex={tabIndex}
      aria-disabled={ariaDisabled}
      aria-label={ariaLabel}
    >
      {children}
    </div>
  )
}
